import type { Route } from './+types/support'

export const meta = (): Route.MetaDescriptors => [
  { title: 'Support | Vital Care Finder' },
  {
    name: 'description',
    content:
      'Answers to common questions about Vital Care Finder — how providers are matched, what happens to your information, and how to reach us about a search or a privacy request.',
  },
]

// Static content page — safe to shared-cache at the CDN edge (see the vit.al
// honor-origin cache policy in sst.config.ts). max-age=0 keeps the browser
// revalidating while s-maxage lets CloudFront serve it from the edge.
export const headers: Route.HeadersFunction = () => ({
  'Cache-Control': 'public, max-age=0, s-maxage=3600, stale-while-revalidate=86400',
})

const supportEmail = (
  <a className="underline" href="mailto:support@vit.al">
    support@vit.al
  </a>
)

// Privacy requests carry statutory response deadlines, so they go to the
// dedicated privacy inbox rather than general support.
const privacyEmail = (
  <a className="underline" href="mailto:privacy@vit.al">
    privacy@vit.al
  </a>
)

export default function Support() {
  return (
    <>
      <header className="space-y-3">
        <h1>Help &amp; support</h1>
        <p>
          Answers to common questions about Vital Care Finder. Still stuck? Email {supportEmail} and we&rsquo;ll get
          back to you.
        </p>
      </header>

      <section id="common-questions">
        <h2>Common questions</h2>

        <h3>How does Vital Care Finder pick care providers?</h3>
        <p>
          Matches are based on the search details you provide - things like the specialty you&rsquo;re looking for,
          where you want to be seen, and your insurance - rather than on ads or paid rankings.
        </p>

        <h3>Is my health information stored?</h3>
        <p>
          Vital only handles the search details needed to find you a care provider. We don&rsquo;t sell consumer health
          data or use it for targeted advertising. See the{' '}
          <a className="underline" href="/privacy">
            Privacy Policy
          </a>{' '}
          for the full picture on what we collect, how long we keep it, and your rights.
        </p>

        <h3>Is this medical advice?</h3>
        <p>
          No. Vital Care Finder is a provider directory, not a source of diagnosis or treatment recommendations. It
          helps you find and contact a clinician - it doesn&rsquo;t replace one.
        </p>
        <p>
          <strong>If this is an emergency, call 911.</strong>
        </p>
      </section>

      <section id="troubleshooting">
        <h2>Troubleshooting</h2>

        <h3>What if I don&rsquo;t see any results?</h3>
        <p>
          Try searching a nearby larger city or ZIP - some areas have few specialists on record - or broaden the
          specialty (e.g. &ldquo;cardiologist&rdquo; instead of a specific subspecialty like &ldquo;interventional
          cardiologist&rdquo;).
        </p>

        <h3>The clinic number didn&rsquo;t work, or the provider information is wrong</h3>
        <p>
          Provider details can change - always confirm accepted insurance directly with the clinic before your visit. If
          a number doesn&rsquo;t connect or something looks wrong, email {supportEmail} with the provider name and
          location and we&rsquo;ll look into it.
        </p>
      </section>

      <section id="privacy-rights">
        <h2>Privacy rights</h2>
        <p>
          To access, correct, delete, or otherwise exercise your privacy rights, email {privacyEmail}. The{' '}
          <a className="underline" href="/privacy">
            Privacy Policy
          </a>{' '}
          describes the rights available to you and how we handle requests.
        </p>
      </section>

      <section id="contact">
        <h2>Get in touch</h2>
        <p>
          For anything not covered above - bug reports, missing or incorrect provider information, or general feedback -
          email {supportEmail}. For privacy requests, email {privacyEmail}. If it&rsquo;s about a specific search,
          include roughly what you looked for (specialty and location), but do not include sensitive health details.
        </p>
      </section>
    </>
  )
}
